import { useEffect, useState } from 'react';
import { act } from '../../lib/actions';
import { api } from '../../lib/api';
import { useNova } from '../../lib/store';
import { Button } from '../ui';
import { AddButton, Cell, ConfirmButton, Drawer, Field, inputClass, Pill, Row, SectionHeader, Table, Toggle, useLoad } from './kit';

type Security = 'tls' | 'starttls' | 'none';
interface Mailbox {
  id: string;
  name: string;
  address: string;
  provider: 'gmail' | 'imap' | 'microsoft';
  imap_host: string;
  imap_port: number;
  imap_security: Security;
  smtp_host: string;
  smtp_port: number;
  smtp_security: Security;
  username: string;
  queue_id: string | null;
  queue_name: string | null;
  signature: string;
  active: boolean;
  checked_at: string | null;
  last_error: string | null;
}
interface Named {
  id: string;
  name: string;
  channel: string;
}

const SECURITY: { id: Security; label: string }[] = [
  { id: 'tls', label: 'SSL/TLS' },
  { id: 'starttls', label: 'STARTTLS' },
  { id: 'none', label: 'None' },
];

/**
 * Mailboxes for the email channel. Nova reads each one's inbox and agents
 * reply from it. Gmail needs an App Password; Microsoft 365 signs in with
 * Microsoft (in a popup); any other provider works with its IMAP and SMTP
 * servers.
 */
export function Mailboxes() {
  const [boxes, reload] = useLoad<Mailbox[]>('/mailboxes');
  const [editing, setEditing] = useState<Mailbox | 'new' | null>(null);
  return (
    <>
      <SectionHeader
        title="Email"
        lead="Mailboxes Nova reads and answers from. New emails go to the mailbox's email queue and on to agents with room under their own email limit (set per user). Nova reads mail that arrives after a mailbox is added."
        action={<AddButton onClick={() => setEditing('new')}>Add mailbox</AddButton>}
      />
      <Table head={['Mailbox', 'Emails go to', 'Last checked', 'Status']} empty={boxes?.length === 0 && 'No mailboxes yet. Add one to start taking email.'}>
        {(boxes ?? []).map((b) => (
          <Row key={b.id} onClick={() => setEditing(b)}>
            <Cell>
              <span className="block font-medium">{b.name}</span>
              <span className="text-[12px] text-ink-3">
                {b.address} · {b.provider === 'gmail' ? 'Gmail' : b.provider === 'microsoft' ? 'Microsoft 365' : b.imap_host}
              </span>
            </Cell>
            <Cell>{b.queue_name ?? <span className="text-break">No queue</span>}</Cell>
            <Cell className="text-ink-2">{b.checked_at ? new Date(b.checked_at).toLocaleString() : 'Not yet'}</Cell>
            <Cell>
              {!b.active ? <Pill>Off</Pill> : b.last_error ? <Pill tone="bad">Error</Pill> : <Pill tone="good">On</Pill>}
              {b.active && b.last_error ? <span className="mt-1 block max-w-[260px] text-[12px] text-break">{b.last_error}</span> : null}
            </Cell>
          </Row>
        ))}
      </Table>
      {editing ? (
        <MailboxDrawer
          box={editing === 'new' ? null : editing}
          onDone={() => {
            setEditing(null);
            reload();
          }}
        />
      ) : null}
    </>
  );
}

function MailboxDrawer({ box, onDone }: { box: Mailbox | null; onDone: () => void }) {
  const [queues] = useLoad<Named[]>('/queues');
  const [providers] = useLoad<{ microsoft: boolean }>('/mailboxes/providers');
  const emailQueues = (queues ?? []).filter((q) => q.channel === 'email');
  const [f, setF] = useState({
    name: box?.name ?? 'Support',
    address: box?.address ?? '',
    provider: box?.provider ?? ('gmail' as Mailbox['provider']),
    username: box?.username ?? '',
    password: '',
    imapHost: box?.provider === 'imap' ? box.imap_host : '',
    imapPort: String(box?.provider === 'imap' ? box.imap_port : 993),
    imapSecurity: (box?.provider === 'imap' ? box.imap_security : 'tls') as Security,
    smtpHost: box?.provider === 'imap' ? box.smtp_host : '',
    smtpPort: String(box?.provider === 'imap' ? box.smtp_port : 465),
    smtpSecurity: (box?.provider === 'imap' ? box.smtp_security : 'tls') as Security,
    queueId: box?.queue_id ?? '',
    signature: box?.signature ?? '',
    active: box?.active ?? true,
  });
  const [busy, setBusy] = useState(false);
  const [checking, setChecking] = useState(false);
  const set = <K extends keyof typeof f>(k: K, v: (typeof f)[K]) => setF((x) => ({ ...x, [k]: v }));
  const gmail = f.provider === 'gmail';
  const ms = f.provider === 'microsoft';
  // Already signed in with Microsoft: settings can change without signing in again
  const signedIn = ms && box?.provider === 'microsoft';

  // The sign-in popup reports back here
  useEffect(() => {
    function onMessage(e: MessageEvent) {
      if (e.origin !== location.origin || !e.data || typeof e.data !== 'object' || !('novaMailbox' in e.data)) return;
      const d = e.data as { novaMailbox: 'connected' | 'error'; message: string };
      setBusy(false);
      useNova.getState().toast(d.message, d.novaMailbox === 'connected' ? 'info' : 'error');
      if (d.novaMailbox === 'connected') onDone();
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [onDone]);

  /** Opens Microsoft's sign-in in a popup; the mailbox is saved when it comes back. */
  async function signInWithMicrosoft() {
    // Opened at once, from the click, so browsers do not block it
    const popup = window.open('', 'nova-microsoft', 'width=520,height=700');
    setBusy(true);
    try {
      const r = await api<{ url: string }>('POST', '/mailboxes/microsoft/start', { name: f.name, queueId: f.queueId, signature: f.signature, active: f.active, mailboxId: box?.id ?? null });
      if (popup) popup.location.href = r.url;
      else window.location.href = r.url;
    } catch (err) {
      popup?.close();
      setBusy(false);
      useNova.getState().toast((err as Error).message, 'error');
    }
  }

  async function save() {
    setBusy(true);
    const body = {
      name: f.name,
      address: f.address,
      provider: f.provider,
      username: f.username || undefined,
      password: f.password || undefined,
      ...(gmail || ms
        ? {}
        : {
            imapHost: f.imapHost,
            imapPort: Number(f.imapPort),
            imapSecurity: f.imapSecurity,
            smtpHost: f.smtpHost,
            smtpPort: Number(f.smtpPort),
            smtpSecurity: f.smtpSecurity,
          }),
      queueId: f.queueId,
      signature: f.signature,
      active: f.active,
    };
    const ok = await act(() => (box ? api('PATCH', `/mailboxes/${box.id}`, body) : api('POST', '/mailboxes', body)), box ? 'Mailbox saved' : 'Mailbox added');
    setBusy(false);
    if (ok) onDone();
  }
  async function checkNow() {
    setChecking(true);
    await act(
      () =>
        api<{ error: string | null }>('POST', `/mailboxes/${box!.id}/check`).then((r) => {
          if (r.error) throw new Error(r.error);
          return true;
        }),
      'Mailbox checked',
    );
    setChecking(false);
  }
  async function remove() {
    const ok = await act(() => api('DELETE', `/mailboxes/${box!.id}`).then(() => true), 'Mailbox removed');
    if (ok) onDone();
  }

  const canStart = f.name.trim().length >= 2 && Boolean(f.queueId);
  const canSave = signedIn ? canStart : f.name.trim().length >= 2 && /.+@.+\..+/.test(f.address) && f.queueId && (box || f.password) && (gmail || (f.imapHost && f.smtpHost));
  return (
    <Drawer
      title={box ? box.name : 'Add a mailbox'}
      open
      onClose={onDone}
      footer={
        <>
          {ms && !signedIn ? (
            <Button tone="primary" className="px-4 py-2" disabled={busy || !canStart || !providers?.microsoft} onClick={() => void signInWithMicrosoft()}>
              {busy ? 'Waiting for Microsoft…' : 'Sign in with Microsoft'}
            </Button>
          ) : (
            <Button tone="primary" className="px-4 py-2" disabled={busy || !canSave} onClick={() => void save()}>
              {busy ? 'Signing in…' : box ? 'Save' : 'Add mailbox'}
            </Button>
          )}
          <Button tone="ghost" className="px-3 py-2" onClick={onDone}>
            Cancel
          </Button>
          {box ? (
            <span className="ml-auto flex gap-2">
              {signedIn ? (
                <Button tone="quiet" className="px-3 py-2" disabled={busy || !providers?.microsoft} onClick={() => void signInWithMicrosoft()}>
                  Sign in again
                </Button>
              ) : null}
              <Button tone="quiet" className="px-3 py-2" disabled={checking || !box.active} onClick={() => void checkNow()}>
                {checking ? 'Checking…' : 'Check now'}
              </Button>
              <ConfirmButton confirmText="Remove it" onConfirm={() => void remove()}>
                Remove
              </ConfirmButton>
            </span>
          ) : null}
        </>
      }
    >
      <p className="mb-4 text-[12px] text-ink-3">
        {ms ? 'Microsoft 365 mailboxes sign in with Microsoft, in a window that opens. Nova never sees the password.' : 'Saving signs in to the mailbox, so a wrong password or server shows here.'}
      </p>
      <Field label="Provider">
        {(id) => (
          <select id={id} className={inputClass} value={f.provider} onChange={(e) => set('provider', e.target.value as Mailbox['provider'])}>
            <option value="gmail">Gmail / Google Workspace</option>
            <option value="microsoft" disabled={!providers?.microsoft && box?.provider !== 'microsoft'}>
              Microsoft 365 / Outlook{providers && !providers.microsoft ? ' (not set up on this Nova)' : ''}
            </option>
            <option value="imap">Other provider (IMAP and SMTP)</option>
          </select>
        )}
      </Field>
      {ms ? (
        <div className="mb-4 rounded-lg border border-line p-3 text-[12px] text-ink-2">
          {signedIn ? (
            <p className="mb-1 text-sm">
              Signed in as <span className="font-medium">{box!.address}</span>
            </p>
          ) : null}
          <p>
            Sign in as the mailbox itself (for example support@yourcompany.com): Nova reads and sends as whoever signs in. Its Microsoft 365 admin must allow IMAP and Authenticated SMTP for it (Microsoft 365 admin center &gt; Users &gt; the mailbox &gt; Mail &gt; Manage email apps).
          </p>
          {providers && !providers.microsoft ? <p className="mt-1 text-break">Microsoft 365 sign-in is not set up on this Nova yet. Ask your provider.</p> : null}
        </div>
      ) : null}
      <Field label="Name" hint="The sender name customers see, like Support">
        {(id) => <input id={id} className={inputClass} value={f.name} onChange={(e) => set('name', e.target.value)} />}
      </Field>
      {!ms ? (
        <>
          <Field label="Email address">
            {(id) => <input id={id} type="email" className={inputClass} value={f.address} onChange={(e) => set('address', e.target.value)} placeholder="support@yourcompany.com" />}
          </Field>
          <Field label={gmail ? 'App Password' : 'Password'} hint={gmail ? 'Not your Google password: Google Account > Security > 2-Step Verification > App passwords. ' + (box ? 'Leave empty to keep the saved one.' : '') : box ? 'Leave empty to keep the saved one.' : undefined}>
            {(id) => <input id={id} type="password" autoComplete="new-password" className={inputClass} value={f.password} onChange={(e) => set('password', e.target.value)} />}
          </Field>
        </>
      ) : null}
      {!gmail && !ms ? (
        <>
          <Field label="Username" hint="Empty: the email address">
            {(id) => <input id={id} className={inputClass} value={f.username} onChange={(e) => set('username', e.target.value)} />}
          </Field>
          <ServerFields label="Incoming mail (IMAP)" host={f.imapHost} port={f.imapPort} security={f.imapSecurity} onHost={(v) => set('imapHost', v)} onPort={(v) => set('imapPort', v)} onSecurity={(v) => set('imapSecurity', v)} placeholder="imap.yourprovider.com" />
          <ServerFields label="Outgoing mail (SMTP)" host={f.smtpHost} port={f.smtpPort} security={f.smtpSecurity} onHost={(v) => set('smtpHost', v)} onPort={(v) => set('smtpPort', v)} onSecurity={(v) => set('smtpSecurity', v)} placeholder="smtp.yourprovider.com" />
        </>
      ) : null}
      <Field label="Emails go to" hint={emailQueues.length ? 'An email queue. Its skills decide who gets the emails.' : 'Add a queue with channel Email under Queues first.'}>
        {(id) => (
          <select id={id} className={inputClass} value={f.queueId} onChange={(e) => set('queueId', e.target.value)}>
            <option value="">Choose an email queue</option>
            {emailQueues.map((q) => (
              <option key={q.id} value={q.id}>
                {q.name}
              </option>
            ))}
          </select>
        )}
      </Field>
      <Field label="Signature" hint="Added under every reply">
        {(id) => <textarea id={id} rows={3} className={inputClass} value={f.signature} onChange={(e) => set('signature', e.target.value)} placeholder={'Kind regards,\nThe Support Team'} />}
      </Field>
      <Toggle label="Mailbox on" checked={f.active} onChange={(v) => set('active', v)} />
    </Drawer>
  );
}

function ServerFields(p: { label: string; host: string; port: string; security: Security; onHost: (v: string) => void; onPort: (v: string) => void; onSecurity: (v: Security) => void; placeholder: string }) {
  return (
    <fieldset className="mb-4 rounded-lg border border-line p-3">
      <legend className="px-1 text-sm font-medium">{p.label}</legend>
      <Field label="Server">{(id) => <input id={id} className={inputClass} value={p.host} onChange={(e) => p.onHost(e.target.value)} placeholder={p.placeholder} />}</Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Port">{(id) => <input id={id} inputMode="numeric" className={inputClass} value={p.port} onChange={(e) => p.onPort(e.target.value.replace(/\D/g, ''))} />}</Field>
        <Field label="Security">
          {(id) => (
            <select id={id} className={inputClass} value={p.security} onChange={(e) => p.onSecurity(e.target.value as Security)}>
              {SECURITY.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.label}
                </option>
              ))}
            </select>
          )}
        </Field>
      </div>
    </fieldset>
  );
}
